import {
  type AuditResource,
  addExtensionToResource,
  type Bundle,
  type CreateReference,
  createAuditResource,
  createCodeableConcept,
  createCoding,
  createExtensionValue,
  createFhirDate,
  createIdentifier,
  DiagnosisTypeCode,
  EncounterClass,
  EncounterService,
  ExtensionUrl,
  findExtensionValue,
  getCodes,
  getFirstCodeFromCodableConcepts,
  getReferenceId,
  getResourcesFromBundle,
  IdentifierCode,
  type Period,
  type RequiredCoding,
  valsOrNone,
} from '@quromedical/fhir-common';
import {
  type ArrayMap,
  compact,
  first,
  flatten,
  last,
  partial,
} from '@quromedical/utils';
import type { EncounterDiagnosis, EncounterStatusHistory } from 'fhir/r4';
import type { DisplayReference } from '../domain/models';
import { getValidReference } from '../mappings';
import { mapFhirObservationToModelPregnancy } from '../observation/mapping';
import { mapFhirToSummaryModelPatient } from '../patient/mapping';
import type { WithId } from '../types';
import { isRequiredCoding } from './extension';
import type {
  AdmissionDiagnoses,
  AdmissionGetResponseWithVitals,
  AdmissionMedicalAidAuthorization,
  AdmissionUpdateFields,
  AdmissionWithPartialPrimaryCode,
} from './schema';

type Icd10CodeType = 'primary' | 'secondary';

const createPrimaryIcd10FlagExtension = createExtensionValue(
  ExtensionUrl.encounterPrimaryIcd10Code,
);

const createOxygenRateExtension = createExtensionValue(
  ExtensionUrl.encounterOxygenRate,
);

const createHospitalizationAuthorizationDateExtension = createExtensionValue(
  ExtensionUrl.encounterHospitalizationAuthorizationDate,
);

const createHospitalizationDependentCodeExtension = createExtensionValue(
  ExtensionUrl.encounterHospitalizationDependentCode,
);

const createHospitalizationSchemeCodeExtension = createExtensionValue(
  ExtensionUrl.encounterHospitalizationSchemeCode,
);

const createHospitalizationSubscriberIdExtension = createExtensionValue(
  ExtensionUrl.encounterHospitalizationSubscriberId,
);

export const isPrimaryIcd10Code = findExtensionValue(
  ExtensionUrl.encounterPrimaryIcd10Code,
);

const getOxygenRate = findExtensionValue(ExtensionUrl.encounterOxygenRate);

const getHospitalizationAuthorizationDate = findExtensionValue(
  ExtensionUrl.encounterHospitalizationAuthorizationDate,
);

const getHospitalizationDependentCode = findExtensionValue(
  ExtensionUrl.encounterHospitalizationDependentCode,
);

const getHospitalizationSchemeCode = findExtensionValue(
  ExtensionUrl.encounterHospitalizationSchemeCode,
);

const getHospitalizationSubscriberId = findExtensionValue(
  ExtensionUrl.encounterHospitalizationSubscriberId,
);

const mapAuthorizationToEncounterHospitalization = (
  medicalAidAuthorization?: AdmissionMedicalAidAuthorization,
): fhir4.EncounterHospitalization | undefined => {
  if (!medicalAidAuthorization) {
    return undefined;
  }

  const { code, coverage, date } = medicalAidAuthorization;
  const { dependantCode, schemeCode, subscriberId } = coverage;

  const extensionList = [
    date ? createHospitalizationAuthorizationDateExtension(date) : undefined,
    createHospitalizationDependentCodeExtension(dependantCode),
    createHospitalizationSchemeCodeExtension(schemeCode),
    createHospitalizationSubscriberIdExtension(subscriberId),
  ];

  return {
    preAdmissionIdentifier: code
      ? createIdentifier(
          IdentifierCode.encounterMedicalAidAuthorizationCode,
          code,
        )
      : undefined,
    extension: compact(extensionList),
  };
};

const mapHospitalizationToMedicalAidAuthorization = (
  hospitalization: fhir4.EncounterHospitalization = {},
): AdmissionMedicalAidAuthorization | undefined => {
  const { extension, preAdmissionIdentifier } = hospitalization;

  const code = preAdmissionIdentifier?.value;
  const date = getHospitalizationAuthorizationDate(extension);
  const dependantCode = getHospitalizationDependentCode(extension);
  const subscriberId = getHospitalizationSubscriberId(extension);
  const schemeCode = getHospitalizationSchemeCode(extension);

  const hasAll = code && date && dependantCode && subscriberId && schemeCode;

  if (!hasAll) {
    return undefined;
  }

  return {
    coverage: {
      dependantCode,
      schemeCode,
      subscriberId,
    },
    code,
    date,
  };
};

const mapOxygenToExtension = (oxygen: fhir4.Quantity): fhir4.Extension =>
  createOxygenRateExtension(oxygen);

export const mapICD10CodeToCodeableConcept = (
  type: Icd10CodeType,
  code: RequiredCoding,
): fhir4.CodeableConcept => {
  const isPrimary = type === 'primary';
  const extension = createPrimaryIcd10FlagExtension(isPrimary);

  const codableConcept = createCodeableConcept(
    code.code,
    code.display,
    code.display,
  );

  return addExtensionToResource(codableConcept, extension);
};

export const getReasonCode = (
  primaryIcd10Code: RequiredCoding,
  icd10Codes: RequiredCoding[] = [],
) => {
  const primaryReasonCode = mapICD10CodeToCodeableConcept(
    'primary',
    primaryIcd10Code,
  );
  const additionalReasonCodes = icd10Codes.map(
    partial(mapICD10CodeToCodeableConcept, 'secondary'),
  );

  const reasonCode = [primaryReasonCode, ...additionalReasonCodes];
  return reasonCode;
};

export const mapCodeableConceptToRequiredCoding = (
  codeableConcept?: fhir4.CodeableConcept,
): RequiredCoding[] => codeableConcept?.coding?.filter(isRequiredCoding) || [];

export const getICD10CodesFromCodeableConcepts = (
  codeableConcepts: (fhir4.CodeableConcept | undefined)[] = [],
): RequiredCoding[] => {
  const codings = codeableConcepts.map(mapCodeableConceptToRequiredCoding);

  return flatten(codings);
};

export const getICD10CodesFromAdmissionDiagnosis = (
  diagnoses: EncounterDiagnosis[] = [],
  diagnosisType: DiagnosisTypeCode,
): string[] => {
  return flatten(
    diagnoses.map((d) => {
      const codings = getICD10CodesFromCodeableConcepts([d.use]);
      const chronicCodes = codings.filter((c) => c.code === diagnosisType);

      if (chronicCodes.length === 0) {
        return [];
      }

      return d.condition.display ? [d.condition.display] : [];
    }),
  );
};

const createExtensions = (
  oxygen?: fhir4.Quantity,
): fhir4.Extension[] | undefined => {
  const oxygenExtension = oxygen ? mapOxygenToExtension(oxygen) : undefined;

  return valsOrNone(compact([oxygenExtension]));
};

const mapLocationToFhirLocation = (
  location: CreateReference | undefined,
): fhir4.EncounterLocation[] | undefined => {
  if (!location) {
    return undefined;
  }

  return [{ location }];
};

const mapFhirLocationToLocationRef = (
  fhirLocation: fhir4.EncounterLocation[],
): CreateReference | undefined => {
  if (!fhirLocation) {
    return undefined;
  }

  const firstLocation = first(fhirLocation);

  return getValidReference(firstLocation?.location);
};

export const updateEncounterFromAdmission = (
  encounter: fhir4.Encounter,
  update: AdmissionUpdateFields,
): fhir4.Encounter => {
  const {
    period,
    medicalAidAuthorization,
    oxygen,
    status,
    service,
    diagnosis = [],
    location,
  } = update;

  return {
    ...encounter,
    period,
    status: status || encounter.status,
    extension: createExtensions(oxygen),
    hospitalization: mapAuthorizationToEncounterHospitalization(
      medicalAidAuthorization,
    ),
    type: [createCodeableConcept(service)],
    diagnosis: mapAdmissionDiagnosisToFhir(diagnosis),
    location: mapLocationToFhirLocation(location),
  };
};

export const mapAdmissionToEncounter = (
  admission: Omit<AdmissionWithPartialPrimaryCode, 'subject'>,
): fhir4.Encounter => {
  const {
    class: cls,
    status,
    patient: subject,
    period,
    service,
    medicalAidAuthorization,
    oxygen,
    statusHistory = [],
    diagnosis = [],
    location,
  } = admission;

  const extension = createExtensions(oxygen);

  return {
    resourceType: 'Encounter',
    class: createCoding(cls),
    status,
    subject,
    period,
    type: [createCodeableConcept(service)],
    hospitalization: mapAuthorizationToEncounterHospitalization(
      medicalAidAuthorization,
    ),
    extension,
    statusHistory,
    diagnosis: mapAdmissionDiagnosisToFhir(diagnosis),
    location: mapLocationToFhirLocation(location),
  };
};

export const updateEncounterFromAdmissionWithId = (
  encounter: WithId<fhir4.Encounter>,
  update: AdmissionUpdateFields,
): WithId<fhir4.Encounter> => {
  const result = updateEncounterFromAdmission(encounter, update);

  return {
    ...result,
    id: encounter.id,
  };
};

export const mapAdmissionToEncounterWithId = (
  admission: WithId<AdmissionWithPartialPrimaryCode>,
): WithId<fhir4.Encounter> => {
  const encounter = mapAdmissionToEncounter(admission);

  return {
    ...encounter,
    id: admission.id,
  };
};

const classCodes = Object.values(EncounterClass);
const serviceCodes = Object.values(EncounterService);

const isCodeOfType =
  <TCode extends string>(codes: TCode[]) =>
  (code?: string): code is TCode => {
    if (!code) {
      return false;
    }

    const isCode = codes.includes(code as TCode);

    return isCode;
  };

const isClassCode = isCodeOfType(classCodes);
const isServiceCode = isCodeOfType(serviceCodes);

/**
 * Get the class code if it exists and is a valid EncounterClass
 */
const getClassCode = (coding?: fhir4.Coding): EncounterClass | undefined => {
  const code = coding?.code;

  const isCode = isClassCode(code);

  return isCode ? code : undefined;
};

/**
 * Get the class code if it exists and is a valid EncounterService
 */
const getServiceCode = (
  codeableConcept?: fhir4.CodeableConcept,
): EncounterService | undefined => {
  const codes = getCodes(codeableConcept);

  const foundCoding = codes.find(isServiceCode);

  return foundCoding;
};

const mapPeriodToAdmissionPeriod = (
  period: fhir4.Period = {},
): Period | undefined => {
  const { start, end } = period;

  if (!start) {
    return undefined;
  }

  return {
    start,
    end,
  };
};

export const mapEncounterToAdmission = (
  encounter: fhir4.Encounter,
  observation?: fhir4.Observation,
): Omit<Partial<AdmissionWithPartialPrimaryCode>, 'subject'> => {
  const {
    class: cls,
    status,
    type,
    subject,
    period,
    hospitalization,
    extension,
    statusHistory = [],
    diagnosis = [],
    location = [],
    reasonCode,
  } = encounter;

  const { icd10Codes, primaryIcd10Code, chronic, acute, reasonOfAdmission } =
    mapDiagnosisToCodes(diagnosis);

  const pregnancyObservation = observation
    ? mapFhirObservationToModelPregnancy(observation)
    : undefined;

  return {
    status,
    class: getClassCode(cls),
    service: getServiceCode(first(type)),
    primaryIcd10Code,
    icd10Codes,
    patient: getValidReference(subject),
    period: mapPeriodToAdmissionPeriod(period),
    medicalAidAuthorization:
      mapHospitalizationToMedicalAidAuthorization(hospitalization),
    oxygen: getOxygenRate(extension),
    statusHistory,
    diagnosis: mapFhirToAdmissionDiagnosis(diagnosis),
    location: mapFhirLocationToLocationRef(location),
    chronic,
    acute,
    reasonOfAdmission,
    statusReason: reasonCode
      ? getFirstCodeFromCodableConcepts(reasonCode)
      : undefined,
    pregnancy: pregnancyObservation,
  };
};

export const mapEncounterToAdmissionWithId = (
  encounter: WithId<fhir4.Encounter>,
  observation?: WithId<fhir4.Observation>,
): WithId<Partial<AdmissionWithPartialPrimaryCode>> => {
  const admission = mapEncounterToAdmission(encounter, observation);

  return {
    ...admission,
    id: encounter.id,
  };
};

export const getFirstAdmissionFromEncounterBundle = (
  bundle: Bundle<WithId<fhir4.Encounter>>,
): WithId<Partial<AdmissionWithPartialPrimaryCode>> | undefined => {
  const resources = getResourcesFromBundle(bundle);
  const encounter = first(resources);

  if (!encounter) {
    return undefined;
  }

  return mapEncounterToAdmissionWithId(encounter);
};

export const finishEncounter = (
  encounter: WithId<fhir4.Encounter>,
  periodEnd: string,
): WithId<fhir4.Encounter> => {
  const period: fhir4.Period | undefined = encounter.period
    ? {
        start: encounter.period.start,
        // end: createFhirDate(periodEnd),
        end: periodEnd,
      }
    : undefined;

  if (!encounter.statusHistory) {
    return {
      ...encounter,
      status: 'finished',
      period,
    };
  }

  const statusHistory = encounter.statusHistory.map<EncounterStatusHistory>(
    (status) => {
      const ispaused =
        status.status === 'in-progress' && status.period.end === undefined;

      const updatePausedStatus: EncounterStatusHistory = {
        period: {
          start: status.period.start,
          end: !ispaused ? status.period.end : periodEnd,
        },
        status: !ispaused ? status.status : 'finished',
      };

      return updatePausedStatus;
    },
  );
  return {
    ...encounter,
    status: 'finished',
    period,
    statusHistory,
  };
};

export const invalidateEncounter = (
  encounter: WithId<fhir4.Encounter>,
): WithId<fhir4.Encounter> => {
  const checkStatus = encounter.status;
  if (checkStatus === 'finished') {
    return encounter;
  }

  const period: fhir4.Period | undefined = encounter.period
    ? {
        start: encounter.period.start,
        end: createFhirDate(new Date()),
      }
    : undefined;

  if (!encounter.statusHistory) {
    return {
      ...encounter,
      status: 'entered-in-error',
      period,
    };
  }

  const statusHistory = encounter.statusHistory.map<EncounterStatusHistory>(
    (status) => {
      const ispaused =
        status.status === 'in-progress' && status.period.end === undefined;

      const updatePausedStatus: EncounterStatusHistory = {
        period: {
          start: status.period.start,
          end: !ispaused ? status.period.end : createFhirDate(new Date()),
        },
        status: !ispaused ? status.status : 'entered-in-error',
      };

      return updatePausedStatus;
    },
  );
  return {
    ...encounter,
    status: 'entered-in-error',
    period,
    statusHistory,
  };
};

export const pauseEncounter = (
  encounter: WithId<fhir4.Encounter>,
): WithId<fhir4.Encounter> => {
  const checkStatus = encounter.status;
  if (checkStatus === 'finished') {
    return encounter;
  }

  const hasEncounterHistory = encounter.statusHistory
    ? encounter.statusHistory
    : [];

  const pausedStatus: EncounterStatusHistory = {
    period: {
      start: createFhirDate(new Date()),
    },
    status: 'onleave',
  };

  if (hasEncounterHistory.length === 0) {
    return {
      ...encounter,
      status: 'onleave',
      statusHistory: [
        {
          period: {
            start: encounter.period?.start,
            end: createFhirDate(new Date()),
          },
          status: 'finished',
        },
        pausedStatus,
      ],
    };
  }

  const statusHistory = hasEncounterHistory.map<EncounterStatusHistory>(
    (status) => {
      const isInProgress =
        status.status === 'in-progress' && status.period.end === undefined;
      const updatePausedStatus: EncounterStatusHistory = {
        period: {
          start: status.period.start,
          end: createFhirDate(new Date()),
        },
        status: 'finished',
      };

      const updatedHistory: EncounterStatusHistory = !isInProgress
        ? status
        : updatePausedStatus;

      return updatedHistory;
    },
  );

  return {
    ...encounter,
    status: 'onleave',
    statusHistory: [...statusHistory, pausedStatus],
  };
};

export const resumeEncounter = (
  encounter: WithId<fhir4.Encounter>,
): WithId<fhir4.Encounter> => {
  if (!encounter.statusHistory) {
    return encounter;
  }
  const needsHistoryChange =
    encounter.statusHistory.length === 0 || encounter.status === 'finished';

  if (needsHistoryChange) {
    return encounter;
  }

  const resumeStatus: EncounterStatusHistory = {
    period: {
      start: createFhirDate(new Date()),
    },
    status: 'in-progress',
  };

  const statusHistory = encounter.statusHistory.map<EncounterStatusHistory>(
    (status) => {
      const ispaused =
        status.status === 'onleave' && status.period.end === undefined;
      const updatePausedStatus: EncounterStatusHistory = {
        period: {
          start: status.period.start,
          end: createFhirDate(new Date()),
        },
        status: status.status,
      };

      const updatedHistory: EncounterStatusHistory = !ispaused
        ? status
        : updatePausedStatus;

      return updatedHistory;
    },
  );

  return {
    ...encounter,
    status: 'in-progress',
    statusHistory: [...statusHistory, resumeStatus],
  };
};

export const startEncounter = (
  encounter: WithId<fhir4.Encounter>,
  periodStart: string,
): WithId<fhir4.Encounter> => {
  const startStatus: EncounterStatusHistory = {
    period: {
      start: periodStart,
    },
    status: 'in-progress',
  };

  const encounterHistory = encounter.statusHistory
    ? encounter.statusHistory
    : [];

  if (encounterHistory.length === 0) {
    return {
      ...encounter,
      status: 'in-progress',
      period: {
        start: periodStart,
      },
      statusHistory: [startStatus],
    };
  }

  const statusHistory = encounterHistory.map<EncounterStatusHistory>(
    (status) => {
      const isPlanned =
        status.status === 'planned' && status.period.end === undefined;
      const updatePlannedStatus: EncounterStatusHistory = {
        period: {
          start: status.period.start,
          end: createFhirDate(new Date()),
        },
        status: status.status,
      };

      const updatedHistory: EncounterStatusHistory = isPlanned
        ? updatePlannedStatus
        : status;

      return updatedHistory;
    },
  );

  return {
    ...encounter,
    status: 'in-progress',
    period: {
      start: periodStart,
    },
    statusHistory: [...statusHistory, startStatus],
  };
};

export const createEncounterAuditResource = (
  resource: fhir4.Encounter,
  method: 'POST' | 'PUT',
  fullUrlKey = 'encounter',
): Required<AuditResource<fhir4.Encounter>> =>
  createAuditResource(resource, method, fullUrlKey);

const createAdmissionSubjectAdder =
  (
    patients: WithId<fhir4.Patient>[],
  ): ArrayMap<AdmissionGetResponseWithVitals, AdmissionGetResponseWithVitals> =>
  (admission) => {
    const patientId = getReferenceId(admission.patient);
    if (!patientId) {
      return admission;
    }

    const patient = patients.find((e) => e.id === patientId);
    if (!patient) {
      return admission;
    }

    const subject = mapFhirToSummaryModelPatient(patient);
    return {
      ...admission,
      subject,
    };
  };

export const addSubjectsToAdmission = (
  admissions: AdmissionGetResponseWithVitals[],
  patients: WithId<fhir4.Patient>[],
): AdmissionGetResponseWithVitals[] => {
  const addSubjectToAdmission = createAdmissionSubjectAdder(patients);

  return admissions.map(addSubjectToAdmission);
};

export const mapCodeableConceptToRequiredCode = (
  codeableConcept?: fhir4.CodeableConcept,
): RequiredCoding | undefined => {
  const codes = first(codeableConcept?.coding?.filter(isRequiredCoding));
  return codes;
};

export const mapAdmissionDiagnosisToFhir = (
  diagnosis: AdmissionDiagnoses[],
): EncounterDiagnosis[] => {
  const diagnoses = diagnosis.map((d) => {
    return {
      condition: d.condition,
      use: createCodeableConcept(d.type || ''),
    };
  });

  return diagnoses;
};

export const mapFhirToAdmissionDiagnosis = (
  diagnosis: EncounterDiagnosis[],
): AdmissionDiagnoses[] => {
  const diagnoses = diagnosis.map<AdmissionDiagnoses>((d) => {
    return {
      condition: getValidReference(d.condition) as DisplayReference,
      type: first(getCodes<DiagnosisTypeCode>(d.use)),
    };
  });

  return diagnoses;
};
const splitAndRemoveColon = (str: string) => {
  const codeDisplay = str.split(':').map((word) => word.trim());
  const code = first(codeDisplay) || '';
  const display = last(codeDisplay) || '';

  return createCoding(code, display);
};

const mapDiagnosisToCodes = (diagnoses: EncounterDiagnosis[]) => {
  const primary = diagnoses.find(
    (code) =>
      first(getCodes<DiagnosisTypeCode>(code.use)) ===
      DiagnosisTypeCode.primary,
  );
  const primaryIcd10Code = primary?.condition.display
    ? splitAndRemoveColon(primary?.condition.display)
    : undefined;
  const icd10Codes = diagnoses
    .filter(
      (code) =>
        first(getCodes<DiagnosisTypeCode>(code.use)) ===
        DiagnosisTypeCode.secondary,
    )
    .map((c) => splitAndRemoveColon(c.condition.display || ''));
  const acute = diagnoses
    .filter(
      (code) =>
        first(getCodes<DiagnosisTypeCode>(code.use)) ===
        DiagnosisTypeCode.acute,
    )
    .map((c) => splitAndRemoveColon(c.condition.display || ''));
  const chronic = diagnoses
    .filter(
      (code) =>
        first(getCodes<DiagnosisTypeCode>(code.use)) ===
        DiagnosisTypeCode.chronic,
    )
    .map((c) => splitAndRemoveColon(c.condition.display || ''));
  const reasonOfAdmission = diagnoses
    .filter(
      (code) =>
        first(getCodes<DiagnosisTypeCode>(code.use)) === DiagnosisTypeCode.rfa,
    )
    .map((c) => splitAndRemoveColon(c.condition.display || ''));

  return {
    primaryIcd10Code,
    icd10Codes,
    acute,
    chronic,
    reasonOfAdmission,
  };
};
